import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { theme } from '@styles/theme';
import { Button } from '../Button';
const Overlay = styled.div `
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transition: all ${theme.transitions.normal};
`;
const sizeMap = {
    sm: '400px',
    md: '500px',
    lg: '700px',
};
const ModalContainer = styled.div `
  background-color: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  width: 100%;
  max-width: ${({ $size }) => sizeMap[$size]};
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  transform: ${({ $isOpen }) => ($isOpen ? 'scale(1)' : 'scale(0.95)')};
  transition: transform ${theme.transitions.normal};
`;
const Header = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.border.primary};
`;
const Title = styled.h2 `
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin: 0;
`;
const CloseButton = styled(Button) `
  padding: ${theme.spacing.xs};
`;
const Content = styled.div `
  padding: ${theme.spacing.lg};
  overflow-y: auto;
  flex: 1;
`;
const Footer = styled.div `
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-top: 1px solid ${theme.colors.border.primary};
`;
export function Modal({ isOpen, onClose, title, children, footer, size = 'md', }) {
    const handleEscape = useCallback((event) => {
        if (event.key === 'Escape') {
            onClose();
        }
    }, [onClose]);
    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, handleEscape]);
    const handleOverlayClick = (event) => {
        if (event.target === event.currentTarget) {
            onClose();
        }
    };
    return (_jsx(Overlay, { "$isOpen": isOpen, onClick: handleOverlayClick, children: _jsxs(ModalContainer, { "$size": size, "$isOpen": isOpen, role: "dialog", "aria-modal": "true", "aria-labelledby": "modal-title", children: [_jsxs(Header, { children: [_jsx(Title, { id: "modal-title", children: title }), _jsx(CloseButton, { variant: "ghost", size: "sm", onClick: onClose, "aria-label": "Close modal", children: _jsx(X, { size: 20 }) })] }), _jsx(Content, { children: children }), footer && _jsx(Footer, { children: footer })] }) }));
}
